import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TRUST_ITEMS = [
  { icon: 'ri-shield-check-line', titleKey: 'trust.item1.title', descKey: 'trust.item1.desc' },
  { icon: 'ri-award-line',        titleKey: 'trust.item2.title', descKey: 'trust.item2.desc' },
  { icon: 'ri-pulse-line',        titleKey: 'trust.item3.title', descKey: 'trust.item3.desc' },
];

const STATS = [
  { val: '150+',   labelKey: 'trust.stat1.label' },
  { val: '€80B+',  labelKey: 'trust.stat2.label' },
  { val: '3',      labelKey: 'trust.stat3.label' },
  { val: '98%',    labelKey: 'trust.stat4.label' },
];

const useInView = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
};

const TrustBlock = () => {
  const { t } = useTranslation();
  const { ref, visible } = useInView();

  return (
    <section id="trust" className="py-20 md:py-28 bg-[#111418]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-medal-line text-green-400 text-sm"></i>
            <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('trust.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4">
            {t('trust.title_p1')} <span className="text-green-400">{t('trust.title_hl')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">{t('trust.subtitle')}</p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {TRUST_ITEMS.map((item, i) => (
            <div key={item.titleKey}
              className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:border-green-500/30 transition-all duration-300"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.5s ease ${i * 0.12}s, transform 0.5s ease ${i * 0.12}s`,
              }}>
              <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 rounded-xl mb-5">
                <i className={`${item.icon} text-green-400 text-2xl`}></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-3">{t(item.titleKey)}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{t(item.descKey)}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-green-900/20 via-green-800/10 to-green-900/20 border border-green-500/10 rounded-2xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map(s => (
              <div key={s.val} className="text-center">
                <div className="text-green-400 font-black text-3xl md:text-4xl mb-1">{s.val}</div>
                <div className="text-white/50 text-sm">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-white/50 text-base mb-6 max-w-lg mx-auto">{t('trust.cta.desc')}</p>
          <button
            onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-400 text-white font-bold px-10 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calculator-line text-lg"></i>
            <span>{t('trust.cta.btn')}</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default TrustBlock;
